import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { hook, withValue } from '@g4/automation-hooks';
import { getPrice } from '@g4/next-furniture/Price/Price';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';
import { useCart } from '../graphql';

import { useTranslations } from './../graphql/components/TranslationsProvider';
import { conditionallyRequired } from '../utils/propTypesUtils';
import useBookingSteps from '../hooks/useBookingSteps';
import { getTripSummary } from '../utils/tripSummaryUtils';
import { darkBlack } from '../theme/theme/colors';

import {
  Button,
  Box,
  Spinner,
  useMedia,
  Text,
  VerticalSpacing,
} from './Boneyard';
import PaylaterMessage from './paypal/PayLaterMessage';
import { useBookingPath } from '../graphql';
import PlainLink from './PlainLink';
import { usePaypalState } from '../components/PaypalStateProvider';

const propTypes = {
  isSkippable: PropTypes.bool,
  loading: PropTypes.bool,
  skipLinkText: conditionallyRequired(
    PropTypes.string,
    props => props.isSkippable
  ),
  noSkipReason: PropTypes.string,
  automationHook: PropTypes.string,
  isContinueDisabled: PropTypes.bool,
  hasPopup: PropTypes.bool,
  popupComponent: conditionallyRequired(
    PropTypes.node,
    props => props.hasPopup
  ),
  disclaimer: PropTypes.node,
  onSubmit: PropTypes.func,
  onSkipLinkClick: PropTypes.func,
  trackSkip: PropTypes.bool,
  scrollRef: PropTypes.shape({ current: PropTypes.any }),
  isSunseekerPromotion: PropTypes.bool,
  setPopupDisplay: PropTypes.func,
  paddingLeft: PropTypes.string,
  displayDisclaimer: PropTypes.bool,
};

const defaultProps = {
  isSkippable: true,
  automationHook: 'page-footer',
  onSkipLinkClick: Function.prototype,
  trackSkip: false,
  setPopupDisplay: Function.prototype,
};

const TripTotalText = styled(Text)`
  text-transform: uppercase;
  font-weight: 600;
  font-size: 16px;
  color: ${darkBlack};
  padding-left: ${props => props.paddingLeft ?? '0px'};
`;

export const ContinueButton = styled(Button)`
  width: 100%;
  min-width: 160px;
  height: 50px;

  ${props => props.theme.query.md} {
    width: auto;
    min-width: 250px;
  }
`;

export const PopupWrapper = styled.div`
  display: block;
  position: absolute;
  top: ${props =>
    props.hidePrice ? '40px' : props.showPaypal ? '56px' : '-40px'};
  right: 0;
  transform: translateY(-100%);
  width: 100%;
  z-index: 1;
  ${props => props.theme.query.md} {
    width: 150%;
  }
`;

const SkipText = styled.span`
  display: block;
  text-align: center;
  font-weight: ${props => (props.isSunseekerPromotion ? 500 : 550)};
`;

const PageFooter = ({
  loading,
  onSubmit,
  hasPopup,
  disclaimer,
  isSkippable,
  skipLinkText,
  noSkipReason,
  popupComponent,
  automationHook,
  onSkipLinkClick,
  isContinueDisabled,
  scrollRef,
  isSunseekerPromotion,
  setPopupDisplay,
  displayDisclaimer = true,
  paddingLeft,
}) => {
  const translations = useTranslations();
  const continueButtonLabel = translations?.common?.continueButtonLabel;

  const { navigateToNextStep } = useBookingSteps();
  const [popupIsVisible, setPopupIsVisible] = React.useState(false);

  const toggleIsPopupVisible = useCallback(() => {
    setPopupIsVisible(prevState => !prevState);
    if (!popupIsVisible) {
      setPopupDisplay(prevState => prevState + 1);
    }
  }, [popupIsVisible, setPopupDisplay, setPopupIsVisible]);

  const popupElementRef = useRef(null);

  useEffect(() => {
    if (popupIsVisible) {
      const popupElement = popupElementRef.current;
      if (popupElement) {
        const rect = popupElement.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY - 80,
          behavior: 'smooth',
        });
      }
    }
  }, [popupIsVisible]);

  const mq = useMedia();

  const navigateToSection = ref => {
    window.scrollTo({
      top: ref.current?.offsetTop,
      left: 0,
      behavior: 'smooth',
    });
  };

  const renderSkip = () => {
    if (!isSkippable && !noSkipReason) {
      return null;
    }

    if (!isSkippable && noSkipReason) {
      return (
        <Box mt={4}>
          <SkipText {...hook(automationHook, 'skip')}>{noSkipReason}</SkipText>
        </Box>
      );
    }

    return (
      <Box mt={6}>
        <PlainLink
          onClick={
            loading
              ? undefined
              : async () => {
                  await onSkipLinkClick();
                  navigateToNextStep();
                }
          }
          {...hook(automationHook, 'skip')}
          isSunseekerPromotion={isSunseekerPromotion}
        >
          <SkipText isSunseekerPromotion={isSunseekerPromotion}>
            {skipLinkText}
          </SkipText>
        </PlainLink>
      </Box>
    );
  };

  const { cart } = useCart();
  const tripTotal = cart?.order?.price?.total ?? 0;
  const balanceDue = cart?.order?.price?.balanceDue ?? 0;

  const { isTravelAgent, isStationAgent, isCallCenterAgent } = useUserContext();
  const isNotAgent = !isStationAgent && !isTravelAgent && !isCallCenterAgent;

  const { isPaypalAvailable, paypalBannersEnabled } = usePaypalState();

  const showPaypal = isNotAgent && isPaypalAvailable && paypalBannersEnabled;

  const { isMinimumElementsForStadiumPackage } =
    (cart?.order && getTripSummary(cart?.order)) || {};

  const { isStadium } = useBookingPath();

  const hidePrice = isStadium && isMinimumElementsForStadiumPackage;

  return (
    <>
      <Box
        d={mq.gt('sm') ? 'flex' : undefined}
        justifyContent={disclaimer ? 'space-between' : 'flex-end'}
        position="relative"
      >
        {displayDisclaimer && (
          <Box flex={2} pr={mq.gt('sm') ? 8 : undefined} mb={4}>
            {disclaimer ?? ''}
          </Box>
        )}
        <Box flex={1}>
          {displayDisclaimer && showPaypal && (
            <>
              <Box d={'flex'} mb={2} justifyContent="space-between">
                <TripTotalText
                  automationHook={withValue(
                    automationHook,
                    'paypal-trip-total-label'
                  )}
                >
                  {translations?.common?.tripTotalLabel} :{' '}
                </TripTotalText>
                <TripTotalText
                  automationHook={withValue(
                    automationHook,
                    'paypal-trip-total'
                  )}
                  paddingLeft={paddingLeft}
                >
                  {hidePrice ? '$ --.--' : getPrice(tripTotal).price}
                </TripTotalText>
              </Box>
              <Box
                d={'flex'}
                automationHook={withValue(
                  automationHook,
                  'paypal-pay-later-message'
                )}
              >
                <PaylaterMessage total={hidePrice ? 0 : balanceDue} />
              </Box>
              <VerticalSpacing size={4} />
            </>
          )}

          <Box d="flex" justifyContent={mq.gte('md') ? 'flex-end' : 'center'}>
            {hasPopup ? (
              <React.Fragment>
                <ContinueButton
                  disabled={isContinueDisabled || loading}
                  automationHook={withValue(automationHook, 'continue-popup')}
                  onClick={toggleIsPopupVisible}
                  fullWidth={mq.lte('sm')}
                >
                  {continueButtonLabel}
                </ContinueButton>

                {popupIsVisible && (
                  <PopupWrapper
                    ref={popupElementRef}
                    showPaypal={showPaypal}
                    hidePrice={hidePrice}
                  >
                    {React.cloneElement(popupComponent, {
                      requestClose: () => {
                        toggleIsPopupVisible();
                        scrollRef && navigateToSection(scrollRef);

                        if (popupComponent.props.requestClose) {
                          popupComponent.props.requestClose();
                        }
                      },
                    })}
                  </PopupWrapper>
                )}
              </React.Fragment>
            ) : (
              <ContinueButton
                disabled={isContinueDisabled || loading}
                htmlType={onSubmit ? undefined : 'submit'}
                onClick={onSubmit}
                automationHook={withValue(automationHook, 'continue')}
                fullWidth={mq.lte('sm')}
              >
                {loading ? (
                  <Spinner
                    size="xsmall"
                    appearance="light"
                    automationHook={withValue(automationHook, 'spinner')}
                  />
                ) : (
                  continueButtonLabel
                )}
              </ContinueButton>
            )}
          </Box>
        </Box>
      </Box>
      {isSkippable && (
        <Box d={'flex'} justifyContent={'flex-end'}>
          {renderSkip()}
        </Box>
      )}
    </>
  );
};

PageFooter.propTypes = propTypes;
PageFooter.defaultProps = defaultProps;

export default PageFooter;
